/**
 * Beweging in de gecentreerde fotohero van een project- of nieuwsdetailpagina.
 *
 * Dezelfde opbouw als de andere hero's, alleen soberder: het beeld zakt uit een lichte
 * overmaat terug op zijn plek, de titel komt regel voor regel van onder een masker vandaan,
 * en de labels erboven en de datum eronder volgen. Geen hap in het beeld en geen kaarten --
 * die horen bij de hero's die een pagina openen, niet bij een artikel.
 *
 * Het splitsen van de titel staat in hero-motion.js, gedeeld met de andere hero's.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles gewoon
 * op zijn plek. Gaat er onverhoopt iets mis nadat de begintoestand is gezet, dan worden de
 * inline stijlen weer weggehaald.
 *
 * Maten: resources/styles/common/hero.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { EASE, minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

export default function initSingleHero() {
  const hero = document.querySelector('[data-single-hero]');

  if (!hero || minderBeweging()) {
    return;
  }

  const media = hero.querySelector('[data-single-hero-media]');
  const body = hero.querySelector('[data-single-hero-body]');
  const heading = hero.querySelector('[data-single-hero-heading]');
  const reveals = Array.from(hero.querySelectorAll('[data-single-hero-reveal]'));

  const beweegbaar = [media, ...reveals].filter(Boolean);

  try {
    if (media) {
      gsap.set(media, { scale: 1.08 });
    }

    gsap.set(reveals, { y: 16, opacity: 0 });

    parallax(hero, media, body);

    const intro = gsap.timeline();

    if (media) {
      intro.to(media, { scale: 1, duration: 2.2, ease: EASE }, 0);
    }

    // De labels staan bóven de titel maar komen erna: eerst waar je bent, dan waar het
    // onder valt.
    if (reveals.length) {
      intro.to(reveals, { y: 0, opacity: 1, duration: 0.9, ease: EASE, stagger: 0.08 }, 0.5);
    }

    onthulRegels(heading, { klasse: 'single-hero__line' });
  } catch (fout) {
    gsap.set(beweegbaar, { clearProps: 'all' });

    throw fout;
  }
}

/**
 * Parallax terwijl de hero uit beeld scrollt. In pixels en niet in procenten: de hero is op
 * mobiel veel hoger dan op desktop, dus procenten zouden daar heel anders uitpakken.
 */
function parallax(hero, media, body) {
  if (!media && !body) {
    return;
  }

  gsap.matchMedia().add('(min-width: 768px)', () => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    if (media) {
      timeline.to(media, { y: 60, ease: 'none' }, 0);
    }

    if (body) {
      timeline.to(body, { y: -40, ease: 'none' }, 0);
    }
  });
}
